/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { Fragment, lazy, Suspense, useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	MemoryManagementSettings,
	MemorySettingToggle,
} from '../../common/ui/memory-management-settings';

import { messages } from './messages';
import { WorkProfileSection } from './work-profile-section';

// Lazy-loaded so the response-and-writing-styles bundle is only fetched when the
// gate is enabled and this section actually renders.
const LazyResponseWritingStyleCard = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/rovo-personalization-response-writing-style-card" */
		'@atlassian/rovo-personalization/ui/response-writing-style-card'
	).then((module) => ({ default: module.ResponseWritingStyleCard })),
);

const ResponseWritingStyleCard = ({ onEdit }: { onEdit?: () => void }): JSX.Element => (
	<Suspense fallback={null}>
		<LazyResponseWritingStyleCard onEdit={onEdit} />
	</Suspense>
);
ResponseWritingStyleCard.displayName = 'lazy(ResponseWritingStyleCard)';

type FlagItem = {
	id: number;
	type: 'success' | 'error';
	title: string;
};

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		maxWidth: '100%',
		minWidth: '0px',
	},
	modalBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		minHeight: '300px',
		minWidth: '0px',
		maxWidth: '100%',
		width: '100%',
		boxSizing: 'border-box',
	},
	settingCard: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

export interface ManageMemoryContentProps {
	allowConversationReference?: boolean;
	allowMemory?: boolean;
	onAllowConversationReferenceChange: (checked: boolean) => void | Promise<void>;
	onAllowMemoryChange: (checked: boolean) => void | Promise<void>;
	learnMoreHref?: string;
	isFetchingSettings?: boolean;
	onManageMemories: () => void;
	onSeeWhatRovoKnows?: () => void;
	onSeeMyActivity?: () => void;
	webSearchDefaultEnabled?: boolean;
	onWebSearchDefaultEnabledChange?: (checked: boolean) => void | Promise<void>;
	/**
	 * Called when the user clicks "Edit response and writing style" on the
	 * response and writing style card. When omitted the card renders but its
	 * button is a no-op. Gated by `rovo_response_and_writing_style_settings`.
	 */
	onEditResponseWritingStyle?: () => void;
}

export const ManageMemoryContent = ({
	allowConversationReference = false,
	allowMemory = false,
	onAllowConversationReferenceChange,
	onAllowMemoryChange,
	learnMoreHref,
	isFetchingSettings,
	onManageMemories,
	onSeeWhatRovoKnows,
	onSeeMyActivity,
	webSearchDefaultEnabled = false,
	onWebSearchDefaultEnabledChange,
	onEditResponseWritingStyle,
}: ManageMemoryContentProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [flags, setFlags] = useState<FlagItem[]>([]);
	const flagIdCounter = useRef(0);

	const addFlag = useCallback((type: FlagItem['type'], title: string) => {
		const id = flagIdCounter.current++;
		setFlags((prev) => [{ id, type, title }, ...prev].slice(0, 5));
	}, []);

	const handleDismissFlag = useCallback((id: string | number) => {
		setFlags((prev) => prev.filter((f) => f.id !== Number(id)));
	}, []);

	const handleSettingsChangeSuccess = useCallback(() => {
		addFlag('success', formatMessage(messages.settingsUpdateSuccessTitle));
	}, [addFlag, formatMessage]);

	const handleSettingsChangeError = useCallback(() => {
		addFlag('error', formatMessage(messages.settingsUpdateErrorTitle));
	}, [addFlag, formatMessage]);

	return (
		<Fragment>
			<Box xcss={styles.root}>
				<Stack space="space.150" xcss={styles.modalBody}>
					{onSeeWhatRovoKnows && (
						<WorkProfileSection
							onSeeWhatRovoKnows={onSeeWhatRovoKnows}
							onSeeMyActivity={onSeeMyActivity}
						/>
					)}
					<MemoryManagementSettings
						allowConversationReference={allowConversationReference}
						allowMemory={allowMemory}
						onAllowConversationReferenceChange={onAllowConversationReferenceChange}
						onAllowMemoryChange={onAllowMemoryChange}
						onManageMemories={onManageMemories}
						learnMoreHref={learnMoreHref}
						onSettingsChangeSuccess={handleSettingsChangeSuccess}
						onSettingsChangeError={handleSettingsChangeError}
						isFetching={isFetchingSettings}
					/>
					{onWebSearchDefaultEnabledChange && (
						<Box xcss={styles.settingCard}>
							<MemorySettingToggle
								id="webSearchDefaultEnabled"
								label={formatMessage(messages.webSearchPreferenceLabel)}
								description={formatMessage(messages.webSearchPreferenceDescription)}
								checked={webSearchDefaultEnabled}
								onChange={onWebSearchDefaultEnabledChange}
								onChangeSuccess={handleSettingsChangeSuccess}
								onChangeError={handleSettingsChangeError}
								isFetching={isFetchingSettings}
							/>
						</Box>
					)}
					{fg('rovo_response_and_writing_style_settings') && (
						<ResponseWritingStyleCard onEdit={onEditResponseWritingStyle} />
					)}
				</Stack>
			</Box>
			<FlagGroup onDismissed={handleDismissFlag}>
				{flags.map((flag) => (
					<AutoDismissFlag
						key={flag.id}
						id={flag.id}
						icon={
							flag.type === 'success' ? (
								<StatusSuccessIcon label="success" color={token('color.icon.success')} />
							) : (
								<StatusErrorIcon label="error" color={token('color.icon.danger')} />
							)
						}
						title={flag.title}
					/>
				))}
			</FlagGroup>
		</Fragment>
	);
};

export default ManageMemoryContent;
